import { useMemo, useState } from "react";
import PropTypes from "prop-types";
import { Button, Drawer, Typography } from "@mui/material";
import { GenericForm } from "common/Components/GenericForm";
import CustomerAccessEditor from "./CustomerAccessEditor";
import { isInviteEmailFormatValid, useInviteEmailExists } from "../hooks/UseInviteEmailExists";
import {
  buildActiveCustomerSelectOptions,
  CUSTOMER_ACCESS_STATUS,
  enrichCustomerAccessRowFromActiveCustomer,
  PLATFORM_USER_ACCESS_LAYOUT,
  PLATFORM_USER_ACCESS_UI,
  isPlatformAdminPlatformRole,
  normalizePlatformRoleValue,
  shouldShowPlatformAdminOptionalCustomerHint,
  validatePlatformCustomerAccess,
} from "../PlatformUserAccess.constants";
import {
  InviteCustomerEditorContainer,
  InviteDrawerContent,
  InviteDrawerFooter,
  InviteDrawerFormSection,
  InviteDrawerHeader,
  InviteDrawerScrollBody,
  InviteFormErrorAlert,
  drawerPaperSx,
} from "./InviteUserModal.styles";

const createEmptyCustomerAccess = () => ({
  customerId: "",
  roles: [],
  accessStatus: CUSTOMER_ACCESS_STATUS.INVITED,
});

const inviteFormFields = (platformRoleOptions) => [
  {
    name: "email",
    label: PLATFORM_USER_ACCESS_UI.inviteEmailLabel,
    type: "text",
    required: true,
    placeholder: PLATFORM_USER_ACCESS_UI.inviteEmailPlaceholder,
  },
  {
    name: "platformRole",
    label: PLATFORM_USER_ACCESS_UI.invitePlatformRoleLabel,
    type: "singleSelect",
    options: platformRoleOptions,
    placeholder: PLATFORM_USER_ACCESS_UI.invitePlatformRoleLabel,
    disableClearable: true,
  }
];

const InviteUserModal = ({
  open,
  onClose,
  onSubmit,
  checkInviteEmail,
  platformRoleOptions,
  roleOptions,
  customers,
}) => {
  const [formValues, setFormValues] = useState({
    email: "",
    platformRole: PLATFORM_USER_ACCESS_UI.defaultPlatformRole,
  });
  const [customerAccessRows, setCustomerAccessRows] = useState([
    createEmptyCustomerAccess(),
  ]);
  const [formErrors, setFormErrors] = useState({});
  const [customerAccessFormError, setCustomerAccessFormError] = useState("");
  const [rowErrors, setRowErrors] = useState([]);

  const { checkEmailOnBlur } = useInviteEmailExists({ checkInviteEmail });

  const platformRole = normalizePlatformRoleValue(formValues.platformRole);
  const showPlatformAdminOptionalHint =
    shouldShowPlatformAdminOptionalCustomerHint(platformRole);

  const customerOptions = useMemo(
    () =>
      buildActiveCustomerSelectOptions({
        customers,
        selectedAccessRows: customerAccessRows,
      }),
    [customers, customerAccessRows]
  );

  const resetState = () => {
    setFormValues({
      email: "",
      platformRole: PLATFORM_USER_ACCESS_UI.defaultPlatformRole,
    });
    setCustomerAccessRows([createEmptyCustomerAccess()]);
    setFormErrors({});
    setCustomerAccessFormError("");
    setRowErrors([]);
  };

  const clearCustomerAccessValidation = () => {
    setCustomerAccessFormError("");
    setRowErrors([]);
  };

  const normalizeRoleValues = (roles) =>
    (roles ?? []).map((role) =>
      typeof role === "string" ? role : role?.value || role?.label
    );

  const normalizeCustomerAccess = () =>
    customerAccessRows
      .filter((row) => row.customerId)
      .map((row) => {
        const enriched = enrichCustomerAccessRowFromActiveCustomer(
          row,
          customers
        );
        return {
          customerId: enriched.customerId,
          customerCode: enriched.customerCode,
          customerName: enriched.customerName,
          roles: normalizeRoleValues(enriched.roles).filter(Boolean),
          accessStatus:
            enriched.accessStatus ?? CUSTOMER_ACCESS_STATUS.INVITED,
        };
      });

  const handleEmailBlur = async (_name, emailValue) => {
    const trimmed = String(emailValue ?? "").trim();
    if (trimmed && !isInviteEmailFormatValid(trimmed)) {
      setFormErrors((previous) => ({
        ...previous,
        email: PLATFORM_USER_ACCESS_UI.invalidEmailError,
      }));
      return;
    }
    const { fieldError } = await checkEmailOnBlur(emailValue);
    if (fieldError) {
      setFormErrors((previous) => ({ ...previous, email: fieldError }));
      return;
    }
    setFormErrors((previous) => {
      if (
        !previous.email ||
        previous.email === PLATFORM_USER_ACCESS_UI.inviteEmailAlreadyExistsError ||
        previous.email === PLATFORM_USER_ACCESS_UI.inviteEmailValidationFailedError ||
        previous.email === PLATFORM_USER_ACCESS_UI.invalidEmailError
      ) {
        const next = { ...previous };
        delete next.email;
        return next;
      }
      return previous;
    });
  };

  const handleSubmit = async () => {
    const email = String(formValues.email ?? "").trim();
    const platformRole = normalizePlatformRoleValue(formValues.platformRole);

    const nextFormErrors = {};
    if (!isInviteEmailFormatValid(email)) {
      nextFormErrors.email = PLATFORM_USER_ACCESS_UI.invalidEmailError;
    }

    const accessValidation = validatePlatformCustomerAccess(
      customerAccessRows,
      platformRole
    );

    const hasAccessIssues =
      Boolean(accessValidation.formError) ||
      accessValidation.rowErrors.some(Boolean);

    if (
      formErrors.email === PLATFORM_USER_ACCESS_UI.inviteEmailAlreadyExistsError ||
      formErrors.email === PLATFORM_USER_ACCESS_UI.inviteEmailValidationFailedError
    ) {
      nextFormErrors.email = formErrors.email;
    }

    if (nextFormErrors.email || hasAccessIssues) {
      setFormErrors(nextFormErrors);
      setCustomerAccessFormError(accessValidation.formError ?? "");
      setRowErrors(accessValidation.rowErrors);
      return;
    }

    if (typeof checkInviteEmail === "function") {
      const { exists, error } = await checkInviteEmail(email);
      if (error) {
        setFormErrors({
          email: PLATFORM_USER_ACCESS_UI.inviteEmailValidationFailedError,
        });
        return;
      }
      if (exists) {
        setFormErrors({
          email: PLATFORM_USER_ACCESS_UI.inviteEmailAlreadyExistsError,
        });
        return;
      }
    }

    setFormErrors({});
    setCustomerAccessFormError("");
    setRowErrors([]);
    const success = await onSubmit({
      email,
      platformRole,
      customerAccess: normalizeCustomerAccess(),
    });
    if (success) {
      resetState();
      onClose();
    }
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={() => {
        resetState();
        onClose();
      }}
      PaperProps={{ sx: drawerPaperSx }}
    >
      <InviteDrawerContent
        $drawerWidth={PLATFORM_USER_ACCESS_LAYOUT.drawerWidth}
      >
        <InviteDrawerHeader>
          <Typography variant="h6">
            {PLATFORM_USER_ACCESS_UI.inviteDrawerTitle}
          </Typography>
        </InviteDrawerHeader>
        <InviteDrawerScrollBody>
          <InviteDrawerFormSection>
            <GenericForm
              fields={inviteFormFields(platformRoleOptions)}
              values={formValues}
              errors={formErrors}
              onChange={(name, value) => {
                setFormValues((previous) => ({ ...previous, [name]: value }));
                if (name === "email") {
                  setFormErrors((previous) => {
                    if (!previous.email) {
                      return previous;
                    }
                    const next = { ...previous };
                    delete next.email;
                    return next;
                  });
                }
                if (name === "platformRole") {
                  clearCustomerAccessValidation();
                }
              }}
              onFieldBlur={(name, value) => {
                if (name === "email") {
                  handleEmailBlur(name, value);
                }
              }}
              showBorder={false}
            />
          </InviteDrawerFormSection>
          <InviteCustomerEditorContainer>
            {showPlatformAdminOptionalHint ? (
              <InviteFormErrorAlert severity="info">
                {PLATFORM_USER_ACCESS_UI.customerAccessOptionalWhenPlatformAdminHint}
              </InviteFormErrorAlert>
            ) : null}
            {customerAccessFormError &&
              !isPlatformAdminPlatformRole(platformRole) ? (
              <InviteFormErrorAlert severity="error">
                {customerAccessFormError}
              </InviteFormErrorAlert>
            ) : null}
            <CustomerAccessEditor
              userId={null}
              showDescription
              rows={customerAccessRows}
              rowErrors={rowErrors}
              customerOptions={customerOptions}
              activeCustomers={customers}
              roleOptions={roleOptions}
              onAddRow={() => {
                clearCustomerAccessValidation();
                setCustomerAccessRows((previous) => [
                  ...previous,
                  createEmptyCustomerAccess(),
                ]);
              }}
              onRemoveRow={(index) => {
                clearCustomerAccessValidation();
                setCustomerAccessRows((previous) =>
                  previous.filter((_, currentIndex) => currentIndex !== index)
                );
              }}
              onRowChange={(index, name, value) => {
                clearCustomerAccessValidation();
                setCustomerAccessRows((previous) =>
                  previous.map((row, currentIndex) => {
                    if (currentIndex !== index) {
                      return row;
                    }
                    if (name === "customerId") {
                      const changed =
                        String(value ?? "") !== String(row.customerId ?? "");
                      const nextRow = changed
                        ? {
                          ...row,
                          customerId: value,
                          roles: [],
                          accessStatus: CUSTOMER_ACCESS_STATUS.INVITED,
                        }
                        : { ...row, customerId: value };
                      return enrichCustomerAccessRowFromActiveCustomer(
                        nextRow,
                        customers
                      );
                    }
                    return { ...row, [name]: value };
                  })
                );
              }}
            />
          </InviteCustomerEditorContainer>
        </InviteDrawerScrollBody>
        <InviteDrawerFooter>
          <Button
            onClick={() => {
              resetState();
              onClose();
            }}
          >
            {PLATFORM_USER_ACCESS_UI.cancelLabel}
          </Button>
          <Button onClick={handleSubmit} variant="contained">
            {PLATFORM_USER_ACCESS_UI.sendInviteLabel}
          </Button>
        </InviteDrawerFooter>
      </InviteDrawerContent>
    </Drawer>
  );
};

InviteUserModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  checkInviteEmail: PropTypes.func,
  platformRoleOptions: PropTypes.arrayOf(PropTypes.object).isRequired,
  roleOptions: PropTypes.arrayOf(PropTypes.object).isRequired,
  customers: PropTypes.arrayOf(PropTypes.object).isRequired,
};

export default InviteUserModal;
